import { FEATURE_CAPTURE_ADDRESS_SELL_SCREEN } from '../../../../constants'
import { hasUnknownLineItems } from '../../../../functions/sale'
import { FulfilmentType } from '../../../../models/fulfilment'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectFulfilmentNoteIsInvalid,
  selectIsParkedSale,
  selectLineItems,
  selectNoteIsInvalid,
  selectSaleFulfilmentDetail,
  selectUnknownLineItems,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  selectHasDiscountError,
  selectHasLineItemError,
  selectHasLineItemFulfilmentNoteError,
  selectHasLineItemNoteError,
  selectHasPriceInputError,
  selectIsFinalisingSale,
  selectIsWaitingForWorkflows,
} from '../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import {
  AddressErrorsStatus,
  useFulfillmentHelper,
} from '../../sale-line-item/fulfilment/popover/hooks/useFulfillmentHelper'
import { useUnfulfilledSaleInvalidCustomer } from '../../sale-unfulfilled/useUnfulfilledSaleInvalidCustomer'
import { useSaleHasServiceOrder } from '../../useSaleHasServiceOrder'
import { useSaleIsContinuedServiceSale } from '../../useSaleIsContinuedServiceSale'

export const useCanTransitionToPaymentScreen = () => {
  const { getFulfillmentAddressStatus } = useFulfillmentHelper()
  const {
    isUnfulfilledSaleInvalidCustomer,
    isUnfulfilledSaleMissingCustomerContact,
  } = useUnfulfilledSaleInvalidCustomer()
  const lineItems = useAppSelector(selectLineItems)
  const unknownLineItems = useAppSelector(selectUnknownLineItems)
  const lineItemError = useAppSelector(selectHasLineItemError)
  // Individual error selectors for service sales bypass
  const hasPriceError = useAppSelector(selectHasPriceInputError)
  const hasDiscountError = useAppSelector(selectHasDiscountError)
  const hasLineItemNoteError = useAppSelector(selectHasLineItemNoteError)
  const hasFulfilmentNoteError = useAppSelector(
    selectHasLineItemFulfilmentNoteError
  )
  const isFinalisingSale = useAppSelector(selectIsFinalisingSale)
  const isWaitingForWorkflows = useAppSelector(selectIsWaitingForWorkflows)

  const fulfilmentNoteInvalid = useAppSelector(selectFulfilmentNoteIsInvalid)
  const lineItemNoteInvalid = useAppSelector(selectNoteIsInvalid)

  const saleHasServiceOrder = useSaleHasServiceOrder()
  const isContinuedServiceSale = useSaleIsContinuedServiceSale()

  const isCaptureAddressSellScreenEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )
  const fulfillmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const isFulfillmentDetailInvalid =
    getFulfillmentAddressStatus(fulfillmentDetail) !==
    AddressErrorsStatus.CORRECTSHIPPING_CORRECTBILLING
  const hasUnfulfiledDeliveryLineItems = lineItems.some(
    lineItem =>
      lineItem.fulfilmentType === FulfilmentType.DELIVERY &&
      !lineItem.fulfilmentCompleted
  )
  const isParkedSale = useAppSelector(selectIsParkedSale)
  const isSavedSale =
    !isParkedSale && lineItems?.some(lineItem => !!lineItem.id)
  const isDeliveryFulfillmentInvalid =
    isCaptureAddressSellScreenEnabled &&
    hasUnfulfiledDeliveryLineItems &&
    isFulfillmentDetailInvalid &&
    !isSavedSale &&
    // Fulfillment validation should not block payment for sales with service orders because jaws deals with the fulfillment for service orders
    !saleHasServiceOrder

  // For service sales, bypass unfulfilled customer validation since JAWS handles fulfillment
  const isUnfulfilledCustomerBlocking =
    !saleHasServiceOrder &&
    (isUnfulfilledSaleInvalidCustomer ||
      isUnfulfilledSaleMissingCustomerContact)

  // For NEW service sales, bypass quantity errors (including NO_INVENTORY) since JAWS handles fulfillment
  // For CONTINUED service sales, don't bypass - user must click "Continue anyway?" to proceed
  // Other line item errors (price, discount, note) should still block payment
  // Per-line UI state error flags (uiStateSlice); *_Invalid below are sale-level aggregates (currentSaleSlice).
  const hasNonQuantityErrors =
    hasPriceError ||
    hasDiscountError ||
    hasLineItemNoteError ||
    hasFulfilmentNoteError
  const isNewServiceSale = saleHasServiceOrder && !isContinuedServiceSale
  const isLineItemErrorBlocking = isNewServiceSale
    ? hasNonQuantityErrors // For new service sales, only block if there are non-quantity errors
    : lineItemError

  return (
    !isFinalisingSale &&
    !isWaitingForWorkflows &&
    lineItems.length &&
    !hasUnknownLineItems({ unknownLineItems }) &&
    !isLineItemErrorBlocking &&
    !fulfilmentNoteInvalid &&
    !lineItemNoteInvalid &&
    !isDeliveryFulfillmentInvalid &&
    !isUnfulfilledCustomerBlocking
  )
}
